if (!customElements.get("el-sticky-cta")) {
  customElements.define(
    "el-sticky-cta",
    class ElStickyCta extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === "true") return;
        this.dataset.ready = "true";

        this.button = this.querySelector("[data-sticky-cta-button]");
        this.textEl = this.querySelector("[data-sticky-cta-text]");
        this.priceEl = this.querySelector("[data-sticky-cta-price]");
        this.comparePriceEl = this.querySelector(
          "[data-sticky-cta-compare-price]",
        );
        this.selectedTitleEl = this.querySelector(
          "[data-sticky-cta-selected-title]",
        );
        this.titleSeparatorEl = this.querySelector(
          "[data-sticky-cta-title-separator]",
        );
        this.defaultButtonText =
          this.dataset.defaultButtonText || "ADD TO CART";
        this.showSelectedTitle = this.dataset.showSelectedTitle === "true";

        this.action = this.dataset.action || "add_to_cart";
        this.targetSelector = this.dataset.targetSelector || "#el-product";
        this.productFormSelector =
          this.dataset.productFormSelector || "el-product-form";
        this.hideBottomOffset = Number(this.dataset.hideBottomOffset || 80);

        this.isTargetVisible = false;
        this.isNearPageEnd = false;
        this.ticking = false;

        this.handleProductUpdate = this.handleProductUpdate.bind(this);
        this.handleButtonClick = this.handleButtonClick.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this.updateVisibility = this.updateVisibility.bind(this);

        document.addEventListener(
          "el-product-form:update",
          this.handleProductUpdate,
        );

        if (this.button) {
          this.button.addEventListener("click", this.handleButtonClick);
        }

        this.setupTargetObserver();

        window.addEventListener("scroll", this.handleScroll, { passive: true });
        window.addEventListener("resize", this.handleScroll);

        customElements.whenDefined("el-product-form").then(() => {
          requestAnimationFrame(() => {
            this.syncFromProductForm();
            this.checkPageEnd();
            this.updateVisibility();
          });
        });
      }

      disconnectedCallback() {
        document.removeEventListener(
          "el-product-form:update",
          this.handleProductUpdate,
        );

        if (this.button) {
          this.button.removeEventListener("click", this.handleButtonClick);
        }

        window.removeEventListener("scroll", this.handleScroll);
        window.removeEventListener("resize", this.handleScroll);

        if (this.targetObserver) {
          this.targetObserver.disconnect();
        }
      }

      setupTargetObserver() {
        const target = document.querySelector(this.targetSelector);

        if (!target || !("IntersectionObserver" in window)) return;

        this.targetObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.isTargetVisible = entry.isIntersecting;
            });

            this.updateVisibility();
          },
          {
            root: null,
            threshold: 0.08,
          },
        );

        this.targetObserver.observe(target);
      }

      handleScroll() {
        if (this.ticking) return;

        this.ticking = true;

        requestAnimationFrame(() => {
          this.checkPageEnd();
          this.updateVisibility();
          this.ticking = false;
        });
      }

      checkPageEnd() {
        const scrollBottom = window.scrollY + window.innerHeight;
        const documentHeight = Math.max(
          document.body.scrollHeight,
          document.documentElement.scrollHeight,
        );

        this.isNearPageEnd =
          scrollBottom >= documentHeight - this.hideBottomOffset;
      }

      updateVisibility() {
        const shouldHide = this.isTargetVisible || this.isNearPageEnd;

        this.classList.toggle("is-hidden", shouldHide);
      }

      getProductForm() {
        return document.querySelector(this.productFormSelector);
      }

      handleProductUpdate(event) {
        const detail = event.detail || {};

        if (this.priceEl && detail.priceHtml) {
          this.priceEl.textContent = detail.priceHtml;
        }

        if (this.comparePriceEl) {
          if (detail.showComparePrice && detail.comparePriceHtml) {
            this.comparePriceEl.textContent = detail.comparePriceHtml;
            this.comparePriceEl.classList.remove("is-hidden");
          } else {
            this.comparePriceEl.textContent = "";
            this.comparePriceEl.classList.add("is-hidden");
          }
        }

        this.updateSelectedTitle(
          detail.bundleTitle ||
            detail.purchaseTitle ||
            detail.selectedTitle ||
            "",
        );

        if (this.button) {
          this.button.disabled = detail.available === false;
        }

        if (this.textEl) {
          this.textEl.textContent =
            detail.available === false ? "SOLD OUT" : this.defaultButtonText;
        }
      }

      syncFromProductForm() {
        const productForm = this.getProductForm();

        if (!productForm) return;

        const price = productForm.querySelector("[data-el-atc-price]");
        const mainButton = productForm.querySelector("[data-el-atc-button]");

        let selectedTitle = "";

        /*
         * New subscription and one-time purchase blocks.
         * This is the most reliable source because it uses the
         * product form's currently selected purchase data.
         */
        if (typeof productForm.getSelectedPurchaseData === "function") {
          const purchaseData = productForm.getSelectedPurchaseData();

          selectedTitle = purchaseData?.title || "";
        }

        /*
         * Fall back to the title already rendered inside
         * the main add-to-cart button.
         */
        if (!selectedTitle) {
          const mainAtcTitle = productForm.querySelector(
            "[data-el-atc-bundle-title]",
          );

          selectedTitle = mainAtcTitle?.textContent?.trim() || "";
        }

        /*
         * Direct markup fallback for the new purchase blocks.
         */
        if (!selectedTitle) {
          const selectedPurchase = productForm.querySelector(
            "[data-purchase-option].is-active",
          );

          if (selectedPurchase?.dataset.purchaseType === "subscription") {
            const selectedSubscriptionVariant = selectedPurchase.querySelector(
              "[data-subscription-variant-option].is-active",
            );

            selectedTitle =
              selectedSubscriptionVariant?.dataset.purchaseTitle ||
              selectedPurchase.dataset.purchaseTitle ||
              "";
          } else {
            selectedTitle = selectedPurchase?.dataset.purchaseTitle || "";
          }
        }

        /*
         * Legacy bundle-option fallback.
         */
        if (!selectedTitle) {
          const selectedBundle = productForm.querySelector(
            "[data-bundle-option].is-active",
          );

          selectedTitle = selectedBundle?.dataset.bundleTitle || "";
        }

        if (price && this.priceEl) {
          this.priceEl.textContent = price.textContent.trim();
        }

        if (mainButton && this.button) {
          this.button.disabled = mainButton.disabled;
        }

        this.updateSelectedTitle(selectedTitle);
      }

      updateSelectedTitle(title) {
        const cleanTitle = String(title || "").trim();
        const shouldShowTitle = this.showSelectedTitle && cleanTitle;

        if (this.selectedTitleEl) {
          this.selectedTitleEl.textContent = shouldShowTitle ? cleanTitle : "";
          this.selectedTitleEl.classList.toggle("is-hidden", !shouldShowTitle);
        }

        if (this.titleSeparatorEl) {
          this.titleSeparatorEl.classList.toggle("is-hidden", !shouldShowTitle);
        }

        this.classList.toggle(
          "has-selected-bundle-title",
          Boolean(shouldShowTitle),
        );
      }

      handleButtonClick(event) {
        event.preventDefault();

        if (this.action === "scroll_to_section") {
          this.scrollToTarget();
          return;
        }

        this.addSelectedBundleToCart();
      }

      scrollToTarget() {
        const target = document.querySelector(this.targetSelector);

        if (!target) return;

        target.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }

      async addSelectedBundleToCart() {
        const productForm = this.getProductForm();

        if (!productForm) {
          this.scrollToTarget();
          return;
        }

        const originalText = this.textEl ? this.textEl.textContent : "";
        const originalDisabled = this.button ? this.button.disabled : false;

        try {
          if (this.button) {
            this.button.disabled = true;
            this.button.classList.add("is-loading");
          }

          if (this.textEl) {
            this.textEl.textContent = "ADDING...";
          }

          if (typeof productForm.onAddToCart === "function") {
            await productForm.onAddToCart();
            this.syncFromProductForm();
            return;
          }

          const mainButton = productForm.querySelector("[data-el-atc-button]");

          if (mainButton) {
            mainButton.click();
          }
        } finally {
          if (this.button) {
            this.button.disabled = originalDisabled;
            this.button.classList.remove("is-loading");
          }

          if (this.textEl) {
            this.textEl.textContent =
              originalText || this.defaultButtonText || "ADD TO CART";
          }

          this.syncFromProductForm();
        }
      }
    },
  );
}
